import {
	CloudWatchLogsClient,
	CreateLogGroupCommand,
	CreateLogStreamCommand,
	DescribeLogGroupsCommand,
	DescribeLogStreamsCommand,
	PutLogEventsCommand,
	type InputLogEvent,
} from "@aws-sdk/client-cloudwatch-logs";
import { fetchAuthSessionTimeout } from "@ptx-autonomous/libs/api";
import { originalConsole } from "./console-override";
import { type AuthSession } from "aws-amplify/auth";

class CloudWatchLogger {
	private client: CloudWatchLogsClient | null = null;
	private logGroupName = "";
	private logStreamName = "";
	private sequenceToken: string | undefined;

	private logQueue: InputLogEvent[] = [];
	private isInitializing = false;
	private isInitialized = false;
	private isStreamInitialized = false; // New flag for stream initialization
	private isFlushing = false;
	private batchTimer: number | null = null;
	private readonly batchInterval = 10000; // 10 seconds
	private userEmail: string | null = null;
	private accountId: string | null = null;

	// Hard limit on maxQueueSize so that we don't spam a crazy amount of recursive logs to CloudWatch
	private readonly maxQueueSize = 50;

	public async updateApiVersion(
		apiVersion: string,
		appVersion: string,
		awsRegion: string,
		logGroupName: string, //e.g. /web/app
	) {
		// Flush any pending logs before switching environments
		await this.flush();

		// Reset the logger state to force reinitialization with new log group
		this.isInitialized = false;
		this.isInitializing = false;
		this.isStreamInitialized = false; // Reset stream flag
		this.client = null;
		this.logGroupName = "";
		this.logStreamName = "";
		this.sequenceToken = undefined;
		this.logQueue = [];

		if (this.batchTimer) {
			clearInterval(this.batchTimer);
			this.batchTimer = null;
		}

		await this.initialize(apiVersion, appVersion, awsRegion, logGroupName);
	}

	public async initialize(
		apiVersion: string,
		appVersion: string,
		awsRegion: string,
		logGroupName: string, //e.g. /web/app
		offlineSession?: AuthSession | null,
	) {
		if (this.isInitialized || this.isInitializing) {
			return;
		}

		this.isInitializing = true;

		try {
			this.logGroupName = `${logGroupName}/${apiVersion}`;
			originalConsole.log(this.logGroupName);

			let session = null;
			const { session: result, sessionFetched } =
				await fetchAuthSessionTimeout();
			if (sessionFetched) {
				session = result;
			} else {
				session = offlineSession;
			}

			if (!session) {
				return originalConsole.error(
					"CloudWatch Logger: No session available",
				);
			}

			const credentials = session.credentials;

			if (!credentials) {
				originalConsole.error(
					"CloudWatch Logger: No credentials available",
				);
				this.isInitializing = false;
				return;
			}

			const email = session.tokens?.idToken?.payload?.email;
			this.userEmail = typeof email === "string" ? email : null;
			if (!this.userEmail) {
				originalConsole.error(
					"CloudWatch Logger: No user email found in session",
				);
				this.isInitializing = false;
				return;
			}

			const accountId =
				session.tokens?.idToken?.payload?.["custom:accountid"];
			this.accountId = typeof accountId === "string" ? accountId : null;
			if (!this.accountId) {
				originalConsole.error(
					"CloudWatch Logger: No account ID found in user attributes",
				);
				this.isInitializing = false;
				return;
			}

			const now = new Date();
			const dateStr = `${now.getFullYear()}/${String(now.getMonth() + 1).padStart(2, "0")}/${String(now.getDate()).padStart(2, "0")}`;
			this.logStreamName = `${dateStr}_[${appVersion}]_${this.accountId}_${this.userEmail}_${Math.random().toString(36).substring(2, 8)}`;
			originalConsole.log(
				`CloudWatch Logger: Using log group "${this.logGroupName}" and log stream "${this.logStreamName}"`,
			);

			this.client = new CloudWatchLogsClient({
				region: awsRegion,
				credentials,
			});

			await this.ensureLogGroupExists();
			// Remove stream creation from here - it will be created when first log comes

			this.isInitialized = true;
			this.isInitializing = false;

			this.startBatching();
		} catch (error) {
			originalConsole.error(
				"CloudWatch Logger: Failed to initialize",
				error,
			);
			this.isInitializing = false;
		}
	}

	private async ensureLogGroupExists() {
		if (!this.client) return;

		try {
			const command = new DescribeLogGroupsCommand({
				logGroupNamePrefix: this.logGroupName,
			});

			const response = await this.client.send(command);

			const groupExists = response.logGroups?.some(
				group => group.logGroupName === this.logGroupName,
			);

			if (!groupExists) {
				await this.client.send(
					new CreateLogGroupCommand({
						logGroupName: this.logGroupName,
					}),
				);
			}
		} catch (error) {
			originalConsole.error(
				"CloudWatch Logger: Failed to ensure log group exists",
				error,
			);
		}
	}

	// New method: only called when logs are actually being sent
	private async ensureLogStreamExists() {
		if (!this.client || this.isStreamInitialized) return;

		try {
			const command = new DescribeLogStreamsCommand({
				logGroupName: this.logGroupName,
				logStreamNamePrefix: this.logStreamName,
			});

			const response = await this.client.send(command);

			const streamExists = response.logStreams?.some(
				stream => stream.logStreamName === this.logStreamName,
			);

			if (!streamExists) {
				await this.client.send(
					new CreateLogStreamCommand({
						logGroupName: this.logGroupName,
						logStreamName: this.logStreamName,
					}),
				);
				originalConsole.log(
					`CloudWatch Logger: Created log stream "${this.logStreamName}"`,
				);
			} else {
				const stream = response.logStreams?.find(
					stream => stream.logStreamName === this.logStreamName,
				);
				this.sequenceToken = stream?.uploadSequenceToken;
				originalConsole.log(
					`CloudWatch Logger: Using existing log stream "${this.logStreamName}"`,
				);
			}

			this.isStreamInitialized = true;
		} catch (error) {
			originalConsole.error(
				"CloudWatch Logger: Failed to ensure log stream exists",
				error,
			);
		}
	}

	private startBatching() {
		if (this.batchTimer) {
			clearInterval(this.batchTimer);
		}
		this.batchTimer = window.setInterval(
			() => this.flush(),
			this.batchInterval,
		);
	}

	public log(level: string, args: unknown[]) {
		// Drop new log if queue is full to preserve root cause logs
		if (this.logQueue.length >= this.maxQueueSize) {
			originalConsole.log(
				"CloudWatch Logger: Queue full, dropping new log to preserve root cause",
			);
			return;
		}

		this.logQueue.push({
			timestamp: Date.now(),
			message: `[${level.toUpperCase()}] ${JSON.stringify(args, this.jsonReplacer, 2)}`,
		});

		// Trigger flush immediately on first log to create stream
		if (!this.isStreamInitialized && this.isInitialized) {
			this.flush();
		}
	}

	public async flush() {
		if (
			this.isFlushing ||
			!this.isInitialized ||
			!this.client ||
			this.logQueue.length === 0
		) {
			return;
		}

		this.isFlushing = true;

		// Clear interval while a flush is in progress to prevent concurrent writes
		if (this.batchTimer) clearInterval(this.batchTimer);

		// Ensure log stream exists before sending logs
		if (!this.isStreamInitialized) {
			await this.ensureLogStreamExists();
		}

		// If stream creation failed, don't try to send logs
		if (!this.isStreamInitialized) {
			originalConsole.error(
				"CloudWatch Logger: Cannot send logs - stream not initialized",
			);
			this.isFlushing = false;
			this.startBatching();
			return;
		}

		// Take the current logs and clear the main queue
		const logEvents = [...this.logQueue];
		this.logQueue = [];

		try {
			const command = new PutLogEventsCommand({
				logGroupName: this.logGroupName,
				logStreamName: this.logStreamName,
				logEvents,
				sequenceToken: this.sequenceToken,
			});

			const response = await this.client.send(command);
			this.sequenceToken = response.nextSequenceToken;
		} catch (error: unknown) {
			// If sending fails, put the logs back at the front of the queue to retry later
			this.logQueue.unshift(...logEvents);
			if (
				error instanceof Error &&
				error.name === "InvalidSequenceTokenException"
			) {
				const awsError = error as Error & {
					expectedSequenceToken?: string;
				};
				if (awsError.expectedSequenceToken) {
					this.sequenceToken = awsError.expectedSequenceToken;
				}
			}
			originalConsole.error(
				"CloudWatch Logger: Failed to send log batch",
				error,
			);
		} finally {
			this.isFlushing = false;
			this.startBatching();
		}
	}

	private jsonReplacer(key: string, value: unknown) {
		if (value instanceof Error) {
			return {
				name: value.name,
				message: value.message,
				stack: value.stack,
			};
		}
		return value;
	}
}

export const cloudWatchLogger = new CloudWatchLogger();
